import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { Send, MessageCircle } from 'lucide-react';
import { usePostActions, useCommentReplies } from '@/hooks/usePosts';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface CommentItemProps {
  comment: any;
  postId: string;
  onReplyAdded?: () => void;
  isReply?: boolean;
}

const CommentItem = ({ comment, postId, onReplyAdded, isReply = false }: CommentItemProps) => {
  const { t } = useTranslation();
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  
  const { addComment, isCommenting } = usePostActions();
  const { toast } = useToast();
  
  const { data: replies, refetch: refetchReplies } = useCommentReplies(
    showReplies ? comment.id : undefined
  );

  const getUserInitials = (user: any) => {
    const name = user?.full_name || user?.display_name;
    if (name) {
      if (!name.includes(' ') && name.includes('.')) {
        const formatted = name.replace(/\./g, ' ');
        return formatted
          .split(' ')
          .map((n: string) => n.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      }
      return name
        .split(' ')
        .map((n: string) => n.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return 'U';
  };

  const getUserDisplayName = (user: any) => {
    const name = user?.full_name || user?.display_name;
    if (name && !name.includes(' ') && name.includes('.')) {
      return name
        .split('.')
        .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    return name || 'User';
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!replyText.trim()) return;
    
    try {
      await addComment({ 
        postId, 
        content: replyText,
        parentCommentId: comment.id 
      });
      
      setReplyText('');
      setShowReplyInput(false);
      toast({
        title: t('comments.replyAdded'),
        description: t('comments.replyAddedSuccess')
      });
      
      if (showReplies) {
        refetchReplies();
      }
      
      onReplyAdded?.();
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.message || t('comments.replyError'),
        variant: "destructive",
      });
    }
  };

  return (
    <div className={cn("flex items-start", isReply && "ml-12")}>
      <Avatar className="h-8 w-8 mr-3" key={comment.user?.avatar_url || comment.user_id}>
        <AvatarImage 
          src={comment.user?.avatar_url} 
          alt={getUserDisplayName(comment.user)}
          loading="eager"
        />
        <AvatarFallback className="bg-pet-purple/20 text-pet-purple text-sm">
          {getUserInitials(comment.user)}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex-1">
        <div className={cn(
          "rounded-lg p-3",
          isReply ? "bg-background" : "bg-muted"
        )}>
          <p className="text-sm">
            <span className="font-bold mr-2">{getUserDisplayName(comment.user)}</span>
            {comment.content}
          </p>
        </div>
        
        <div className="flex items-center gap-4 mt-1 px-3">
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
          </span>
          
          {!isReply && (
            <button
              onClick={() => setShowReplyInput(!showReplyInput)}
              className="text-xs font-semibold text-muted-foreground hover:text-primary transition-colors"
            >
              {t('comments.reply')}
            </button>
          )}
          
          {!isReply && comment.replies_count > 0 && (
            <button
              onClick={() => setShowReplies(!showReplies)}
              className="text-xs font-semibold text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
            >
              <MessageCircle className="h-3 w-3" />
              {showReplies 
                ? (comment.replies_count === 1 ? t('comments.hideReply', { count: comment.replies_count }) : t('comments.hideReplies', { count: comment.replies_count }))
                : (comment.replies_count === 1 ? t('comments.viewReply', { count: comment.replies_count }) : t('comments.viewReplies', { count: comment.replies_count }))
              }
            </button>
          )}
        </div>
        
        {showReplyInput && (
          <form onSubmit={handleReplySubmit} className="mt-2 flex items-center gap-2">
            <Input
              placeholder={t('comments.replyTo', { name: getUserDisplayName(comment.user) })}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              className="text-sm"
              disabled={isCommenting}
            />
            <Button
              type="submit"
              size="sm"
              disabled={!replyText.trim() || isCommenting}
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        )}
        
        {showReplies && replies && replies.length > 0 && (
          <div className="mt-3 space-y-3">
            {replies.map((reply: any) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                postId={postId}
                onReplyAdded={() => {
                  refetchReplies();
                  onReplyAdded?.();
                }}
                isReply={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentItem;
